import React, { useState } from 'react';
import { GridRowModes, GridRowEditStopReasons } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import useAgentConfigs from './hooks/useAgentConfigs';
import AiAgentService from '../../api/AiAgentService';
import { 
    ConfigsDataGrid, 
    AddConfigDialog, 
    DeleteConfirmDialog, 
    NotificationSnackbar,
    PromptEditor,
    AdditionalSettings
} from './components';
import { DEFAULT_CONFIG, VALIDATION_MESSAGES } from './constants';
import './aiSupportSettings.css';

const AiSupportSettings = ({ children }) => {
    const {
        agentConfigs,
        loading,
        error,
        createAgentConfig,
        updateAgentConfig,
        deleteAgentConfig,
        canDeleteConfig
    } = useAgentConfigs();

    const [rowModesModel, setRowModesModel] = useState({});
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedConfigId, setSelectedConfigId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [newConfig, setNewConfig] = useState(DEFAULT_CONFIG);
    const [regeneratingDoc, setRegeneratingDoc] = useState(false);
    const [regeneratingAllDoc, setRegeneratingAllDoc] = useState(false);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleDeleteClick = (id) => () => {
        if (!canDeleteConfig(id)) {
            showSnackbar(VALIDATION_MESSAGES.CANNOT_DELETE_DEFAULT, 'error');
            return;
        }
        setSelectedConfigId(id);
        setDeleteDialogOpen(true);
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const processRowUpdate = async (newRow) => {
        try {
            const updatedRow = await updateAgentConfig(newRow);
            showSnackbar(VALIDATION_MESSAGES.CONFIG_UPDATED);
            return updatedRow;
        } catch (error) {
            showSnackbar(VALIDATION_MESSAGES.UPDATE_ERROR, 'error');
            throw error;
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleAddNew = () => {
        setNewConfig({ ...DEFAULT_CONFIG });
        setAddDialogOpen(true);
    };

    const handleAddSubmit = async () => {
        try {
            if (!newConfig.name || !newConfig.chat_model) {
                showSnackbar(VALIDATION_MESSAGES.REQUIRED_FIELDS, 'error');
                return;
            }

            await createAgentConfig(newConfig);
            setAddDialogOpen(false);
            showSnackbar(VALIDATION_MESSAGES.CONFIG_CREATED);
        } catch (error) {
            showSnackbar(VALIDATION_MESSAGES.CREATE_ERROR, 'error');
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteAgentConfig(selectedConfigId);
            setDeleteDialogOpen(false);
            setSelectedConfigId(null);
            showSnackbar(VALIDATION_MESSAGES.CONFIG_DELETED);
        } catch (error) {
            showSnackbar(VALIDATION_MESSAGES.DELETE_ERROR, 'error');
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleRegenerateDocumentation = async () => {
        setRegeneratingDoc(true);
        try {
            const response = await AiAgentService.regenerateDocumentation({});
            if (response.success) {
                showSnackbar(response.message, 'success');
            } else {
                showSnackbar(response.message, 'error');
            }
        } catch (error) {
            showSnackbar('Помилка при регенерації документації', 'error');
        } finally {
            setRegeneratingDoc(false);
        }
    };

    const handleRegenerateAllDocumentation = async () => {
        setRegeneratingAllDoc(true);
        try {
            const response = await AiAgentService.regenerateAllDocumentation({});
            if (response.success) {
                showSnackbar(response.message, 'success');
            } else {
                showSnackbar(response.message, 'error');
            }
        } catch (error) {
            showSnackbar('Помилка при регенерації документації для всіх мов', 'error');
        } finally {
            setRegeneratingAllDoc(false);
        }
    };

    if (error) {
        return (
            <div className="ai-support-settings">
                <h2>Налаштування AI підтримки</h2>
                <div className="error-message">
                    {error}
                </div>
                {children}
            </div>
        );
    }

    return (
        <div className="ai-support-settings">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Налаштування AI підтримки</h2>
                {/* <Button
                    variant="contained"
                    startIcon={<FaPlus />}
                    onClick={handleAddNew}
                    sx={{ mb: 2 }}
                >
                    Додати конфігурацію
                </Button> */}
            </Box>

            <ConfigsDataGrid
                agentConfigs={agentConfigs}
                loading={loading}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                onProcessRowUpdate={processRowUpdate}
                onEditClick={handleEditClick}
                onSaveClick={handleSaveClick}
                onCancelClick={handleCancelClick}
                onDeleteClick={handleDeleteClick}
                
            />

            {/* Редактор промпту */}
            <PromptEditor />

            <AddConfigDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                newConfig={newConfig}
                setNewConfig={setNewConfig}
                onSubmit={handleAddSubmit}
            />

            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
            />

            <NotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
            />

            <AdditionalSettings 
                regeneratingDoc={regeneratingDoc}
                regeneratingAllDoc={regeneratingAllDoc}
                onRegenerateDocumentation={handleRegenerateDocumentation}
                onRegenerateAllDocumentation={handleRegenerateAllDocumentation}
            />

            {children}
        </div>
    );
};

export default AiSupportSettings;